/**
 * Augmentation effect utilities
 * Resolves how augmentations modify piece behavior (movement, promotion, win conditions)
 */

import { getAugmentation } from "../augmentations/registry";
import type {
  Augmentation,
  AugmentationEffect,
  CaptureModifierCondition,
} from "../types/augmentation";
import type { Color, PieceType, PromotablePieceType } from "../types/common";
import type { ChessState } from "../types/game-state";
import type { Piece } from "../types/piece";

/**
 * Get active augmentations that affect a specific piece
 */
export function getAugmentationsForPiece(
  state: ChessState,
  piece: Piece,
): Augmentation[] {
  const playerAugs =
    piece.color === "WHITE"
      ? (state.augmentations?.white?.active ?? [])
      : (state.augmentations?.black?.active ?? []);

  return playerAugs
    .map((id) => getAugmentation(id))
    .filter(
      (aug): aug is Augmentation =>
        aug?.targetPieceTypes?.includes(piece.type) ?? false,
    );
}

/**
 * Get all active augmentation effects for a player
 */
export function getActiveEffects(
  state: ChessState,
  color: Color,
): AugmentationEffect[] {
  const playerAugs =
    color === "WHITE"
      ? (state.augmentations?.white?.active ?? [])
      : (state.augmentations?.black?.active ?? []);

  return playerAugs
    .map((id) => getAugmentation(id))
    .filter((aug): aug is Augmentation => aug !== undefined)
    .map((aug) => aug.effect);
}

/**
 * Check if a piece has a phase movement augmentation
 */
export function hasPhaseMovement(state: ChessState, piece: Piece): boolean {
  const augmentations = getAugmentationsForPiece(state, piece);
  return augmentations.some((aug) => aug.effect.type === "PHASE_MOVEMENT");
}

/**
 * Get the effective movement type for a piece, considering MOVEMENT_SWAP augmentations.
 * PAWN and KING are never swapped.
 */
export function getEffectiveMovementType(
  state: ChessState,
  piece: Piece,
): PieceType {
  if (piece.type === "PAWN" || piece.type === "KING") {
    return piece.type;
  }

  const effects = getActiveEffects(state, piece.color);
  for (const effect of effects) {
    if (effect.type === "MOVEMENT_SWAP") {
      for (const pair of effect.swapPairs) {
        if (piece.type === pair.pieceA) return pair.pieceB;
        if (piece.type === pair.pieceB) return pair.pieceA;
      }
    }
  }

  return piece.type;
}

/**
 * Get 0-indexed rank indices where a pawn triggers promotion when moving forward.
 * Default: [6] for white (moving to rank 8), [1] for black (moving to rank 1).
 * With Acceleration: adds one rank earlier.
 */
export function getPromotionRankIndices(
  state: ChessState,
  piece: Piece,
): number[] {
  const defaultRank = piece.color === "WHITE" ? 6 : 1;
  const ranks = [defaultRank];

  const effects = getActiveEffects(state, piece.color);
  for (const effect of effects) {
    if (effect.type === "PROMOTION_RANK_OVERRIDE") {
      // promotionRankIndex = the 0-indexed rank the pawn is ON when it triggers promotion.
      // For white: pawn on rank (whiteRank-1) promotes by moving to whiteRank.
      //   0-indexed: (whiteRank-1) - 1 = whiteRank - 2
      //   e.g. whiteRank=7 -> index 5 -> pawn on rank 6 promotes to rank 7
      // For black: pawn on rank (blackRank+1) promotes by moving to blackRank.
      //   0-indexed: (blackRank+1) - 1 = blackRank
      //   e.g. blackRank=2 -> index 2 -> pawn on rank 3 promotes to rank 2
      const overrideIndex =
        piece.color === "WHITE" ? effect.whiteRank - 2 : effect.blackRank;
      if (!ranks.includes(overrideIndex)) {
        ranks.push(overrideIndex);
      }
    }
  }

  return ranks;
}

/**
 * Returns true if the given piece is immune to capture due to a KING_ADJACENT_IMMUNITY augment.
 * A piece is immune when:
 *   - Its player has a KING_ADJACENT_IMMUNITY effect active
 *   - The piece is within `radius` squares (Chebyshev) of its own king
 *   - The piece is not the king itself
 */
export function isKingAdjacentImmune(state: ChessState, piece: Piece): boolean {
  if (piece.type === "KING") return false;

  const effects = getActiveEffects(state, piece.color);
  let radius: number | null = null;

  for (const effect of effects) {
    if (effect.type === "KING_ADJACENT_IMMUNITY") {
      radius = effect.radius;
      break;
    }
  }

  if (radius === null) return false;

  const king = state.pieces.find(
    (p) => p.type === "KING" && p.color === piece.color,
  );
  if (!king) return false;

  const fileDist = Math.abs(
    piece.position.file.charCodeAt(0) - king.position.file.charCodeAt(0),
  );
  const rankDist = Math.abs(piece.position.rank - king.position.rank);

  return fileDist <= radius && rankDist <= radius;
}

/**
 * Returns true if the given piece is affected by a REVERSE_MOVEMENT augment.
 * For PAWN pieces: grants backward movement (handled in move generation).
 * For QUEEN pieces: caps sliding range to 3 squares (handled in move generation).
 */
export function hasReverseMovement(state: ChessState, piece: Piece): boolean {
  const augmentations = getAugmentationsForPiece(state, piece);
  return augmentations.some((aug) => aug.effect.type === "REVERSE_MOVEMENT");
}

/**
 * Returns true if the given PAWN has a DUAL_KNIGHT_PROMOTION augment active.
 * When true, the pawn's promotion is overridden: it becomes a Knight and a second
 * Knight is placed on an adjacent empty square.
 */
export function hasDualKnightPromotion(
  state: ChessState,
  piece: Piece,
): boolean {
  if (piece.type !== "PAWN") return false;
  const augmentations = getAugmentationsForPiece(state, piece);
  return augmentations.some(
    (aug) => aug.effect.type === "DUAL_KNIGHT_PROMOTION",
  );
}

/**
 * Returns true if the given PAWN has a DUAL_MINOR_PROMOTION augment active.
 * When true, the pawn's promotion is overridden: it becomes a Bishop and a Knight
 * is placed on an adjacent empty square.
 */
export function hasDualMinorPromotion(
  state: ChessState,
  piece: Piece,
): boolean {
  if (piece.type !== "PAWN") return false;
  const augmentations = getAugmentationsForPiece(state, piece);
  return augmentations.some(
    (aug) => aug.effect.type === "DUAL_MINOR_PROMOTION",
  );
}

/**
 * Returns true if the given piece is immune to capture due to an UNMOVED_IMMUNITY augment.
 * A piece is immune when:
 *   - Its player has an UNMOVED_IMMUNITY effect active
 *   - The piece has never moved (hasMoved === false)
 *   - The piece is not the king itself
 */
export function isUnmovedImmune(state: ChessState, piece: Piece): boolean {
  if (piece.type === "KING") return false;
  if (piece.hasMoved) return false;

  const effects = getActiveEffects(state, piece.color);
  return effects.some((effect) => effect.type === "UNMOVED_IMMUNITY");
}

/**
 * Returns true if the given player has a SACRIFICE augment active.
 * When a pawn belonging to that player is captured, the most-advanced remaining
 * ally pawn instantly promotes to Queen.
 */
export function hasSacrificeAug(state: ChessState, color: Color): boolean {
  const effects = getActiveEffects(state, color);
  return effects.some((effect) => effect.type === "SACRIFICE");
}

/**
 * Returns true if the given PAWN has a MORPH_PROMOTION augment active.
 * When true, the pawn's promotion options are expanded to include any enemy
 * piece type that has been captured during the game.
 */
export function hasMorphPromotion(state: ChessState, piece: Piece): boolean {
  if (piece.type !== "PAWN") return false;
  const augmentations = getAugmentationsForPiece(state, piece);
  return augmentations.some((aug) => aug.effect.type === "MORPH_PROMOTION");
}

/**
 * Returns the set of extra promotion types available via PN-012 Morph Promotion.
 * These are the unique enemy piece types that `color` has captured so far.
 * King is always excluded. Returns an empty array if PN-012 is not active.
 */
export function getMorphPromotionTypes(
  state: ChessState,
  color: Color,
): PromotablePieceType[] {
  const effects = getActiveEffects(state, color);
  if (!effects.some((e) => e.type === "MORPH_PROMOTION")) return [];

  const captured =
    color === "WHITE"
      ? (state.capturedPieceTypes?.white ?? [])
      : (state.capturedPieceTypes?.black ?? []);

  // Deduplicate and exclude KING and PAWN (KING can't be captured; PAWN is not promotable)
  return [...new Set(captured)].filter(
    (t) => t !== "KING" && t !== "PAWN",
  ) as PromotablePieceType[];
}

/**
 * Returns true if the given player has a RISING_QUEEN augment active.
 * When the player's Queen is captured, a countdown begins; after the delay,
 * the most-advanced remaining pawn promotes to Queen (once per game).
 */
export function hasRisingQueenAug(state: ChessState, color: Color): boolean {
  const effects = getActiveEffects(state, color);
  return effects.some((effect) => effect.type === "RISING_QUEEN");
}

/**
 * Returns the RISING_QUEEN delay (in full turns) for the given player, or null if not active.
 */
export function getRisingQueenDelay(
  state: ChessState,
  color: Color,
): number | null {
  const effects = getActiveEffects(state, color);
  for (const effect of effects) {
    if (effect.type === "RISING_QUEEN") return effect.delayTurns;
  }
  return null;
}

/**
 * Returns true if the given KING has a KING_FURY augment active.
 * When true, the king can capture pieces that are normally immune to capture.
 */
export function hasKingFury(state: ChessState, piece: Piece): boolean {
  if (piece.type !== "KING") return false;
  const augmentations = getAugmentationsForPiece(state, piece);
  return augmentations.some((aug) => aug.effect.type === "KING_FURY");
}

/**
 * Returns true if the given player has a MINOR_REVIVAL augment active.
 * When the first Knight or Bishop is captured, a countdown begins; after the delay
 * the piece revives at its canonical start square (once per game).
 */
export function hasMinorRevivalAug(state: ChessState, color: Color): boolean {
  const effects = getActiveEffects(state, color);
  return effects.some((effect) => effect.type === "MINOR_REVIVAL");
}

/**
 * Returns the MINOR_REVIVAL delay (in full turns) for the given player, or null if not active.
 */
export function getMinorRevivalDelay(
  state: ChessState,
  color: Color,
): number | null {
  const effects = getActiveEffects(state, color);
  for (const effect of effects) {
    if (effect.type === "MINOR_REVIVAL") return effect.delayTurns;
  }
  return null;
}

/**
 * Returns true if the given ROOK has a ROOK_LINE_CAPTURE augment active.
 * When true, the rook can pass through one enemy piece (capturing it en route) and
 * continue to any empty square or a second enemy piece beyond it.
 */
export function hasRookLineCapture(state: ChessState, piece: Piece): boolean {
  if (piece.type !== "ROOK") return false;
  const augmentations = getAugmentationsForPiece(state, piece);
  return augmentations.some((aug) => aug.effect.type === "ROOK_LINE_CAPTURE");
}

/**
 * Returns true if the given player has an ASSASSIN augment active.
 * When true, that player's Knights can capture Queens regardless of immunity effects
 * (KING_ADJACENT_IMMUNITY, UNMOVED_IMMUNITY).
 */
export function hasAssassinAug(state: ChessState, color: Color): boolean {
  const effects = getActiveEffects(state, color);
  return effects.some((effect) => effect.type === "ASSASSIN");
}

/**
 * Returns true if the given Knight is in a permanently weakened state.
 * A Knight becomes weakened after capturing a Queen with the KN-004 ASSASSIN augment.
 * A weakened Knight that captures any non-Queen piece causes mutual destruction.
 */
export function isKnightWeakened(state: ChessState, piece: Piece): boolean {
  if (piece.type !== "KNIGHT") return false;
  const colorKey = piece.color === "WHITE" ? "white" : "black";
  return state.weakenedKnightIds?.[colorKey]?.includes(piece.id) ?? false;
}

/**
 * Returns true if the given player has a LAST_PAWN_MOVEMENT augment active.
 * When true, and exactly 1 ally pawn remains, that pawn can always double-advance
 * from any rank (path must be clear).
 */
export function hasLastPawnMovement(state: ChessState, color: Color): boolean {
  const effects = getActiveEffects(state, color);
  return effects.some((effect) => effect.type === "LAST_PAWN_MOVEMENT");
}

/**
 * Returns true if the given PAWN is the only remaining pawn of its color.
 * Used by LAST_PAWN_MOVEMENT to determine if the movement buff is active.
 */
export function isLastPawn(state: ChessState, piece: Piece): boolean {
  if (piece.type !== "PAWN") return false;
  const pawns = state.pieces.filter(
    (p) => p.type === "PAWN" && p.color === piece.color,
  );
  return pawns.length === 1;
}

/**
 * Returns true if `c` is strictly between `a` and `b` (exclusive).
 */
function isBetween(a: number, b: number, c: number): boolean {
  return (a < c && c < b) || (b < c && c < a);
}

/**
 * Returns true if the given piece is immune due to a SHIELD_WALL augment.
 * A piece is immune when:
 *   - Its player has a SHIELD_WALL effect active
 *   - The piece is strictly between an ally rook and the ally king on the
 *     same rank or file (the rook and king are the wall's endpoints and are
 *     not themselves protected)
 *   - The piece is not the king itself
 */
export function isShieldWallImmune(state: ChessState, piece: Piece): boolean {
  if (piece.type === "KING") return false;

  const effects = getActiveEffects(state, piece.color);
  if (!effects.some((e) => e.type === "SHIELD_WALL")) return false;

  const king = state.pieces.find(
    (p) => p.type === "KING" && p.color === piece.color,
  );
  if (!king) return false;

  const rooks = state.pieces.filter(
    (p) => p.type === "ROOK" && p.color === piece.color,
  );

  const pieceFile = piece.position.file.charCodeAt(0);
  const pieceRank = piece.position.rank;
  const kingFile = king.position.file.charCodeAt(0);
  const kingRank = king.position.rank;

  for (const rook of rooks) {
    const rookFile = rook.position.file.charCodeAt(0);
    const rookRank = rook.position.rank;

    // Same rank: rook, king, and piece all on the same rank
    if (rookRank === kingRank && pieceRank === kingRank) {
      if (isBetween(rookFile, kingFile, pieceFile)) return true;
    }

    // Same file: rook, king, and piece all on the same file
    if (rookFile === kingFile && pieceFile === kingFile) {
      if (isBetween(rookRank, kingRank, pieceRank)) return true;
    }
  }

  return false;
}

/**
 * Get all active augmentations (full objects) for a player.
 * Unlike `getActiveEffects` (which returns only effects), this returns the
 * complete `Augmentation` including `targetPieceTypes` and `category`.
 */
export function getActiveAugmentations(
  state: ChessState,
  color: Color,
): Augmentation[] {
  const playerAugs =
    color === "WHITE"
      ? (state.augmentations?.white?.active ?? [])
      : (state.augmentations?.black?.active ?? []);

  return playerAugs
    .map((id) => getAugmentation(id))
    .filter((aug): aug is Augmentation => aug !== undefined);
}

/**
 * Evaluate a CaptureModifierCondition for a given attacker piece.
 */
export function evaluateCaptureCondition(
  state: ChessState,
  piece: Piece,
  condition: CaptureModifierCondition,
): boolean {
  switch (condition.type) {
    case "ADJACENT_TO_ALLY_KING": {
      const king = state.pieces.find(
        (p) => p.type === "KING" && p.color === piece.color,
      );
      if (!king) return false;
      const fileDist = Math.abs(
        piece.position.file.charCodeAt(0) - king.position.file.charCodeAt(0),
      );
      const rankDist = Math.abs(piece.position.rank - king.position.rank);
      return fileDist <= condition.radius && rankDist <= condition.radius;
    }
    case "PIECE_ALIVE": {
      const colorKey = piece.color === "WHITE" ? "white" : "black";
      // For QN-001: use the permanent queenMajestyLost flag
      if (condition.pieceType === "QUEEN") {
        const lost = state.queenMajestyLost?.[colorKey] ?? false;
        return condition.whenAlive ? !lost : lost;
      }
      // Generic: check if at least one piece of the type is on the board
      const alive = state.pieces.some(
        (p) => p.type === condition.pieceType && p.color === piece.color,
      );
      return condition.whenAlive ? alive : !alive;
    }
  }
}

/**
 * Calculate the capture success chance for an attacker capturing a defender.
 *
 * Formula: captureChance = clamp(1.0 + SUM(attackerModifiers) - SUM(defenderEvasion), 0, 1)
 *
 * Bypasses:
 *   - King with KING_FURY always captures at 100% (skip defender evasion)
 *   - Knight with ASSASSIN capturing a Queen always captures at 100%
 *
 * Returns 1.0 when no probability augments are in play.
 */
export function calculateCaptureChance(
  state: ChessState,
  attackerPiece: Piece,
  defenderPiece: Piece,
): number {
  // Bypass: King Fury — king always captures at 100%
  if (attackerPiece.type === "KING" && hasKingFury(state, attackerPiece)) {
    return 1.0;
  }

  // Bypass: Assassin Knight capturing Queen — always 100%
  if (
    attackerPiece.type === "KNIGHT" &&
    defenderPiece.type === "QUEEN" &&
    hasAssassinAug(state, attackerPiece.color)
  ) {
    return 1.0;
  }

  // Collect attacker CAPTURE_MODIFIER effects
  let attackerBonus = 0;
  const attackerAugs = getActiveAugmentations(state, attackerPiece.color);
  for (const aug of attackerAugs) {
    if (aug.effect.type !== "CAPTURE_MODIFIER") continue;
    // Check if augment applies to this piece type (empty targetPieceTypes = all pieces)
    if (
      aug.targetPieceTypes.length > 0 &&
      !aug.targetPieceTypes.includes(attackerPiece.type)
    )
      continue;

    if (aug.effect.condition) {
      const conditionMet = evaluateCaptureCondition(
        state,
        attackerPiece,
        aug.effect.condition,
      );
      attackerBonus += conditionMet
        ? aug.effect.modifier
        : (aug.effect.modifierWhenFalse ?? 0);
    } else {
      attackerBonus += aug.effect.modifier;
    }
  }

  // Collect defender DEFENSE_MODIFIER effects
  let defenderEvasion = 0;
  const defenderAugs = getActiveAugmentations(state, defenderPiece.color);
  for (const aug of defenderAugs) {
    if (aug.effect.type !== "DEFENSE_MODIFIER") continue;
    if (
      aug.targetPieceTypes.length > 0 &&
      !aug.targetPieceTypes.includes(defenderPiece.type)
    )
      continue;
    defenderEvasion += aug.effect.evasion;
  }

  const chance = 1.0 + attackerBonus - defenderEvasion;
  return Math.max(0, Math.min(1, chance));
}

/**
 * Check if consecutive check win condition is met.
 * Scans both players' active augmentations for CONSECUTIVE_CHECK_WIN effects.
 */
export function checkConsecutiveCheckWin(state: ChessState): {
  ended: boolean;
  result: "WHITE_WINS" | "BLACK_WINS";
  reason: string;
} | null {
  for (const color of ["WHITE", "BLACK"] as const) {
    const colorKey = color === "WHITE" ? "white" : "black";
    const checks = state.consecutiveChecks[colorKey];
    if (checks === 0) continue;

    const effects = getActiveEffects(state, color);
    for (const effect of effects) {
      if (
        effect.type === "CONSECUTIVE_CHECK_WIN" &&
        checks >= effect.checkCount
      ) {
        return {
          ended: true,
          result: color === "WHITE" ? "WHITE_WINS" : "BLACK_WINS",
          reason: "consecutive check",
        };
      }
    }
  }

  return null;
}
